// Orders table columns definition for reuse
import React, { useState } from 'react';
import { Typography, Chip, Box, IconButton, Tooltip } from '@mui/material';
import { Visibility, CloudUpload } from '@mui/icons-material';
import ReactDOM from 'react-dom';

// Global preview state (for simplicity, can be improved with context)
let setPreviewImage = null;

export function ImagePreviewPortal() {
  const [preview, setPreview] = useState(null);
  setPreviewImage = setPreview;
  if (!preview) return null;
  return ReactDOM.createPortal(
    <Box sx={{
      position: 'fixed',
      top: '50%',
      right: 32,
      transform: 'translateY(-50%)',
      zIndex: 2000,
      bgcolor: 'white',
      boxShadow: 6,
      borderRadius: 2,
      p: 1,
      border: '2px solid #DC143C',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img src={preview} alt="preview" referrerPolicy="no-referrer" style={{ maxWidth: 400, maxHeight: 400, borderRadius: 8 }} />
    </Box>,
    document.body
  );
}

const getStatusString = (status, t) => {
  switch (status) {
    case 1: return t('orders:status.unpaid');
    case 2: return t('orders:status.processing');
    case 3: return t('orders:status.purchased');
    case 4: return t('orders:status.inWarehouse');
    case 5: return t('orders:status.shippedOut');
    case 6: return t('orders:status.inNigeria');
    case 7: return t('orders:status.received');
    case 8: return t('orders:status.refunded');
    case 9: return t('orders:status.success');
    case 10: return t('orders:status.closed');
    default: return t('orders:status.unknown');
  }
};
const getStatusColor = (status) => {
  // Use numeric status values directly for color determination
  const statusNum = typeof status === 'number' ? status : 
    (status === 'Success' || status === 'success' ? 9 :
     status === 'Processing' || status === 'processing' ? 2 :
     status === 'Unpaid' || status === 'unpaid' ? 1 :
     status === 'Refunded' || status === 'refunded' ? 8 :
     status === 'Closed' || status === 'closed' ? 10 : null);
  
  switch (statusNum) {
    case 9: return '#4CAF50'; // Success
    case 2: return '#FF9800'; // Processing
    case 1: return '#9E9E9E'; // Unpaid
    case 8: return '#F44336'; // Refunded
    case 10: return '#757575'; // Closed
    default: return '#2196F3';
  }
};
const getStatusIcon = (status) => {
  // You can customize icons per status if desired
  return <span />;
};

const getLastFourDigits = (phoneValue) => {
  const normalized = `${phoneValue || ''}`.replace(/\D/g, '');
  if (!normalized) return '-';
  return normalized.slice(-4);
};

const formatWarehouseNumber = (warehouseNumber) => {
  const rawValue = `${warehouseNumber || ''}`.trim();
  if (!rawValue) return '-';
  const normalized = rawValue.replace(/-/g, '');
  if (normalized.length <= 6) return normalized;
  return `${normalized.slice(0, 6)}-${normalized.slice(6)}`;
};

export function getOrderColumns({ onView, onUpload, t }) {
  return [
    {
      field: 'sn',
      headerName: t('orders:table.sn'),
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: 'user',
      headerName: t('orders:table.user'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {params && params.row && params.row.user && params.row.user.username ? params.row.user.username : ''}
        </Typography>
      )
    },
    // {
    //   field: 'phone',
    //   headerName: t('orders:table.phone'),
    //   width: 150,
    //   renderCell: (params) => (
    //     <Typography variant="body2">{params && params.row && params.row.user && params.row.user.phone ? params.row.user.phone : ''}</Typography>
    //   )
    // },
   
    {
      field: 'pic_url',
      headerName: t('orders:table.picture'),
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <img
            src={params.value}
            alt="pic"
            referrerPolicy="no-referrer"
            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
            onMouseEnter={() => setPreviewImage && setPreviewImage(params.value)}
            onMouseLeave={() => setPreviewImage && setPreviewImage(null)}
          />
        ) : null
      )
    },
    {
      field: 'bin_code',
      headerName: t('orders:table.binCode'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params?.row?.user?.bin_code || '-'}</Typography>
      )
    },
      {
      field: 'total_quantity',
      headerName: t('orders:table.qty'),
      width: 80,
      renderCell: (params) => (
        <Typography variant="body2">{params && params.row && params.row.total_quantity !== undefined ? params.row.total_quantity : ''}</Typography>
      )
    },
      {
      field: 'warehouse_number',
      headerName: t('orders:table.warehouseNumber'),
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2">{(params.value)}</Typography>
      )
    },
    //  {
    //   field: 'phone_last_four',
    //   headerName: t('orders:table.phoneLast4'),
    //   width: 120,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Typography variant="body2">{getLastFourDigits(params?.row?.user?.phone)}</Typography>
    //   )
    // },
    {
      field: 'title',
      headerName: t('orders:table.title'),
      width: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            lineHeight: 1.4,
            maxHeight: '2.8em',
          }}
        >
          {params.value}
        </Typography>
      )
    },
 
    {
      field: 'order_number',
      headerName: t('orders:table.orderNumber'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      )
    },
    {
      field: 'is_demo',
      headerName: t('orders:table.orderMode'),
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('orders:mode.staffDemo') : t('orders:mode.liveOrder')}
          size="small"
          color={params.value ? 'warning' : 'success'}
          variant={params.value ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'purchasing_account',
      headerName: t('orders:table.purchasingAccount'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },

    
    {
      field: 'created_at',
      headerName: t('orders:table.orderDate'),
      width: 160,
      renderCell: (params) => {
        const value = params && params.row && params.row.created_at;
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: false
          });
        } catch (error) {
          return 'N/A';
        }
      }
    },
    {
      field: 'updated_at',
      headerName: t('orders:table.updatedTime'),
      width: 160,
      renderCell: (params) => {
        const value = params && params.row && params.row.updated_at;
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: false
          });
        } catch (error) {
          return 'N/A';
        }
      }
    },
    {
      field: 'rate',
      headerName: t('orders:table.rate'),
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params && params.row && params.row.rate !== undefined ? `₦${params.row.rate}` : ''}
        </Typography>
      )
    },
    {
      field: 'price',
      headerName: t('orders:table.price'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params && params.row && params.row.price !== undefined ? `¥${params.row.price}` : ''}
        </Typography>
      )
    },
  
    
    {
      field: 'total_price_raw',
      headerName: t('orders:table.total'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params && params.row && params.row.total_price !== undefined ? `¥${params.row.total_price}` : ''}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: t('orders:table.status'),
      width: 150,
      renderCell: (params) => {
        const statusStr = typeof params.value === 'number' ? getStatusString(params.value, t) : params.value;
        return (
          <Chip
            icon={getStatusIcon(params.value)}
            label={statusStr}
            size="small"
            sx={{
              backgroundColor: getStatusColor(params.value),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    {
      field: 'goods_type',
      headerName: t('orders:table.goodsType'),
      width: 140,
      renderCell: (params) => {
        const goodsType = params.value || 'normal';
        const isSpecial = goodsType === 'special';
        return (
          <Chip
            label={isSpecial ? t('orders:goodsType.special') : t('orders:goodsType.normal')}
            size="small"
            sx={{
              backgroundColor: isSpecial ? '#FF9800' : '#4CAF50',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem'
            }}
          />
        );
      }
    },
    {
      field: 'is_group_buy',
      headerName: t('orders:table.groupBuy'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('orders:groupBuy.yes') : t('orders:groupBuy.no')}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4CAF50' : '#9E9E9E',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        />
      )
    },
    {
      field: 'group_buy_pool',
      headerName: t('orders:table.poolId'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('orders:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('orders:tooltips.viewDetails')}>
            <IconButton color="primary" onClick={() => onView && onView(params.row)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          {/* Only show upload icon for Purchased orders (status = 3) that are NOT group buy */}
          {params.row.status === 3 && !params.row.is_group_buy && (
            <Tooltip title={t('orders:tooltips.uploadWarehousePhotos')}>
              <IconButton
                color="success"
                onClick={() => onUpload && onUpload(params.row)}
                sx={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  }
                }}
              >
                <CloudUpload />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];
}

export default getOrderColumns; 
